import React, { useState, useRef } from 'react'
import { ZinButton, ZinInput, ZinIcon } from '@tnl/zin-react'
import { IOwner } from '@features/account/types'
import { useAuthHeaders } from '@hooks/useAuthHeaders'
import { updateAccountPassword } from '@features/account/services/MkApiAccount'
import { getPasswordChecks } from '@utils/password'
import { PasswordRequirements } from '@account/components/PasswordRequirements/PasswordRequirements'
import {
  PROFILE_CANCEL_BUTTON_TEXT,
  PROFILE_SAVE_BUTTON_TEXT,
  PROFILE_CURRENT_PASSWORD_LABEL,
  PROFILE_NEW_PASSWORD_LABEL,
  PROFILE_PASSWORD_SUCCESS_MESSAGE,
  PROFILE_PASSWORD_ERROR_MESSAGE,
} from '@constants'
import { useSnackbar } from '@contexts/SnackbarContext'
import type { IMkPasswordUpdateResponse } from '@account/types/editInfoForm'
import './ChangePasswordForm.scss'

export interface IChangePasswordFormProps {
  owner: IOwner
  onCancel: () => void
  onSave: () => void
}

export const ChangePasswordForm: React.FC<IChangePasswordFormProps> = ({ owner, onCancel, onSave }) => {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [isCurrentPasswordFocused, setIsCurrentPasswordFocused] = useState(false)
  const [isNewPasswordFocused, setIsNewPasswordFocused] = useState(false)
  const [currentPasswordError, setCurrentPasswordError] = useState<string | null>(null)
  const [newPasswordError, setNewPasswordError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const currentPasswordToggleRef = useRef<HTMLButtonElement>(null)
  const newPasswordToggleRef = useRef<HTMLButtonElement>(null)
  const authHeaders = useAuthHeaders()
  const { showSnackbar } = useSnackbar()

  // Check if form can be saved
  const passwordChecks = getPasswordChecks(newPassword, 8)
  const allPasswordRequirementsMet = Object.values(passwordChecks).every((check) => check)
  const canSave = currentPassword && newPassword && allPasswordRequirementsMet && !isSubmitting

  const handleCurrentPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setCurrentPassword(value)
    // Clear error when user starts typing
    if (currentPasswordError) {
      setCurrentPasswordError(null)
    }
  }

  const setErrors = (currentPasswordError: string | null, newPasswordError: string | null) => {
    setCurrentPasswordError(currentPasswordError)
    setNewPasswordError(newPasswordError)
  }

  const processErrorResponse = (response: IMkPasswordUpdateResponse) => {
    const { failedFieldName, displayMessage } = response || {}
    if (failedFieldName && displayMessage) {
      switch (failedFieldName) {
        case 'oldPassword':
          setErrors(displayMessage, null)
          break
        case 'newPassword':
          setErrors(null, displayMessage)
          break
        default:
          setErrors(null, PROFILE_PASSWORD_ERROR_MESSAGE)
      }
    } else {
      setErrors(null, PROFILE_PASSWORD_ERROR_MESSAGE)
    }
  }

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    setErrors(null, null)

    const formState = {
      oldPassword: currentPassword,
      newPassword: newPassword,
    }

    try {
      const response = await updateAccountPassword(formState, authHeaders)

      if (response?.success === true) {
        // Success case
        setCurrentPassword('')
        setNewPassword('')
        setShowNewPassword(false)
        setErrors(null, null)

        // Show success message
        showSnackbar({
          message: PROFILE_PASSWORD_SUCCESS_MESSAGE,
          type: 'success',
          duration: 3,
        })

        onSave()
      } else {
        processErrorResponse(response)
      }
    } catch (error) {
      // Handle any other errors (network, etc.)
      console.error('Error updating password:', error)
      setErrors(null, PROFILE_PASSWORD_ERROR_MESSAGE)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form
      className="mk-profile__password-form"
      onSubmit={handleFormSubmit}>
      <div className="mk-profile__password-form-row">
        <ZinInput
          id="current-password"
          name="currentPassword"
          label={PROFILE_CURRENT_PASSWORD_LABEL}
          value={currentPassword}
          onInputChange={handleCurrentPasswordChange}
          type={showCurrentPassword ? 'text' : 'password'}
          required
          status={currentPasswordError ? 'error' : undefined}
          statusText={currentPasswordError || undefined}
          onFocus={() => setIsCurrentPasswordFocused(true)}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            // Don't hide the button if the focus is moving to the toggle button
            if (e.relatedTarget !== currentPasswordToggleRef.current) {
              setIsCurrentPasswordFocused(false)
            }
          }}
        />
        {isCurrentPasswordFocused && (
          <button
            ref={currentPasswordToggleRef}
            type="button"
            className="mk-profile__password-toggle"
            onClick={() => setShowCurrentPassword(!showCurrentPassword)}>
            <ZinIcon
              name={showCurrentPassword ? 'eye' : 'eye-slash'}
              set="fal"
            />
          </button>
        )}
      </div>
      <div className="mk-profile__password-form-row">
        <ZinInput
          id="new-password"
          name="newPassword"
          label={PROFILE_NEW_PASSWORD_LABEL}
          value={newPassword}
          onInputChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewPassword(e.target.value)}
          type={showNewPassword ? 'text' : 'password'}
          required
          status={newPasswordError ? 'error' : undefined}
          statusText={newPasswordError || undefined}
          onFocus={() => setIsNewPasswordFocused(true)}
          onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
            // Don't hide the button if the focus is moving to the toggle button
            if (e.relatedTarget !== newPasswordToggleRef.current) {
              setIsNewPasswordFocused(false)
            }
          }}
        />
        {isNewPasswordFocused && (
          <button
            ref={newPasswordToggleRef}
            type="button"
            className="mk-profile__password-toggle"
            onClick={() => setShowNewPassword(!showNewPassword)}>
            <ZinIcon
              name={showNewPassword ? 'eye' : 'eye-slash'}
              set="fal"
            />
          </button>
        )}
      </div>
      <div className="mk-profile__password-requirements">
        <PasswordRequirements password={newPassword} />
      </div>
      <div className="mk-profile__password-form-actions">
        <ZinButton
          variant="link"
          onClick={onCancel}
          disabled={isSubmitting}>
          {PROFILE_CANCEL_BUTTON_TEXT}
        </ZinButton>
        <ZinButton
          variant="primary"
          type="submit"
          disabled={!canSave}
          loading={isSubmitting}>
          {PROFILE_SAVE_BUTTON_TEXT}
        </ZinButton>
      </div>
    </form>
  )
}
